import { apiClient } from '@/auth';

/**
 * Mediterranean diet score for the requested range. MNT swagger:19826.
 * `total_score` and `max_score` are the headline numbers; the per-component
 * fields (vegetable_score, fruit_score, etc.) drive the linear progress
 * bars next to the center donut on v1's Summary page.
 *
 * Algorithm version: '2' adds `sugary_beverage_score` and
 * `ultraprocessed_desserts_score`; '1' is the legacy 8-component version.
 */
export type MediterraneanScore = {
  vegetable_score: number;
  fruit_score: number;
  nuts_seeds_score: number;
  desserts_score?: number | null;
  whole_grains_score: number;
  fish_score: number;
  legume_score: number;
  fat_score: number;
  red_meat_score: number;
  total_score: number;
  max_score: number;
  alcohol_score?: number | null;
  sugary_beverage_score?: number | null;
  ultraprocessed_desserts_score?: number | null;
  version?: string | null;
};

export type MediterraneanHistoryRow = {
  log_date: string;
  window_start_date: string;
  window_end_date: string;
  total_score: number | null;
  max_score: number;
  has_data: boolean;
};

export type MediterraneanHistory = {
  patient_id: number;
  date_since: string;
  date_until: string;
  window_days: number;
  version: '1' | '2';
  daily_mediterranean_scores: MediterraneanHistoryRow[];
};

export const metricsApi = {
  /**
   * Aggregate Mediterranean score for the date range — what v1's Summary page
   * renders as the center donut + linear progress bars.
   *
   * GET /metrics/patients/{patient_id}/mediterranean (MNT swagger:10653).
   * `disaggregated=true` matches v1's call (clinician/src/pages/patient-summary.tsx:556).
   */
  async mediterranean(patientId: number, params: { date_since: string; date_until: string }): Promise<MediterraneanScore | null> {
    try {
      const res = await apiClient.get<MediterraneanScore>(`/metrics/patients/${patientId}/mediterranean`, {
        params: {
          date_since: params.date_since,
          date_until: params.date_until,
          disaggregated: true,
          use_ranking_filter: false,
        },
      });
      return res.data;
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 403 || status === 404) return null;
      throw err;
    }
  },

  /**
   * Rolling-window history of the Mediterranean score over the date range.
   * Useful for a time-series chart (planned but not surfaced in v1's Summary
   * page itself; v1 uses the aggregate above). Kept here for future Log v2
   * or trend cards.
   *
   * GET /metrics/patients/{patient_id}/mediterranean/history (MNT swagger:10675).
   */
  async mediterraneanHistory(patientId: number, params: { date_since: string; date_until: string; window_days?: number }): Promise<MediterraneanHistory | null> {
    try {
      const res = await apiClient.get<MediterraneanHistory>(`/metrics/patients/${patientId}/mediterranean/history`, {
        params: {
          date_since: params.date_since,
          date_until: params.date_until,
          window_days: params.window_days ?? 7,
        },
      });
      return res.data;
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 403 || status === 404) return null;
      throw err;
    }
  },
};
